
import { useRecommendedProducts } from "@/hooks/useRecommendedProducts";
import { Loader2, Calendar, Users, MapPin, Clock, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import { usePerformanceTracking } from "@/lib/performance-monitor";
import { useQueryClient } from "@tanstack/react-query";
import CompactTourCalendar from "@/components/CompactTourCalendar";
import DOMPurify from "dompurify";

const RecommendedCourses = () => {
  usePerformanceTracking('RecommendedCourses');
  const queryClient = useQueryClient();
  
  const { data: recommendedProducts, isLoading, error, isSuccess, isFetching, failureCount, failureReason } = useRecommendedProducts();
  
  // 상태 변화 추적을 위한 디버깅 로그
  useEffect(() => {
    console.log('🔄 RecommendedCourses 상태 변화:', {
      isLoading,
      isSuccess,
      isFetching,
      hasData: !!recommendedProducts,
      dataLength: recommendedProducts?.length,
      error: error?.message,
      failureCount,
      failureReason: failureReason?.message,
      timestamp: new Date().toISOString()
    });
    
    // 10초 이상 로딩 중이면 경고
    if (isLoading && !isSuccess) {
      const timeout = setTimeout(() => {
        if (isLoading && !isSuccess) {
          console.error('⚠️ 추천 상품 로딩이 10초 이상 지속됨');
        }
      }, 10000);
      return () => clearTimeout(timeout);
    }
  }, [isLoading, isSuccess, isFetching, recommendedProducts, error, failureCount, failureReason]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', { 
      month: 'long', 
      day: 'numeric',
      weekday: 'short'
    });
  };

  const formatPrice = (price: number) => {
    return price.toLocaleString('ko-KR');
  };
  
  const handleRefresh = () => {
    console.log('🔄 수동 새로고침 시도');
    queryClient.invalidateQueries({ queryKey: ['recommended-products'] });
  };

  return (
    <section data-testid="course-recommendations" className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
            <span>🎯</span>
            <span>지금 예약 가능</span>
          </div>
          <h3 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            지금 예약 가능한 추천 투어
          </h3>
          <p className="text-lg text-gray-600">
            곧 출발하는 인기 투어를 지금 바로 예약하세요
          </p>
        </div>

        {error ? (
          <div className="text-center py-12">
            <p className="text-red-600 mb-2">투어 정보를 불러오는 데 실패했습니다.</p>
            <p className="text-gray-600">{error.message}</p>
          </div>
        ) : isLoading ? (
          <div className="text-center py-12">
            <div className="flex justify-center items-center mb-4">
              <Loader2 className="w-8 h-8 animate-spin text-[#F39800]" />
              <span className="ml-2 text-gray-600">예약 가능한 투어를 불러오는 중...</span>
            </div>
            {failureCount > 0 && (
              <button
                onClick={handleRefresh}
                className="mt-4 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#F39800]"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                다시 시도
              </button>
            )}
          </div>
        ) : recommendedProducts && recommendedProducts.length > 0 ? (
          <div className="flex flex-wrap justify-center gap-8">
            {recommendedProducts.map((product) => (
              <div 
                key={product.product_id}
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 overflow-hidden w-full max-w-sm"
              >
                <div className="relative">
                  <img 
                    src={product.main_photo_url || product.courses?.[0]?.main_photo_url || "/images/image1.webp"} 
                    alt={product.name}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                      예약 가능
                    </span>
                  </div>
                  <div className="absolute top-4 right-4">
                    <span className="bg-white/90 backdrop-blur-sm text-gray-800 px-3 py-1 rounded-full text-sm font-bold">
                      ₩{formatPrice(product.price)}
                    </span>
                  </div>
                </div>

                <div className="p-6">
                  <h4 className="text-xl font-bold text-gray-800 mb-3 leading-tight">
                    {product.name}
                  </h4>

                  <div 
                    className="text-gray-600 text-sm mb-4 line-clamp-2 min-h-[2.5rem]"
                    dangerouslySetInnerHTML={{ 
                      __html: DOMPurify.sanitize(product.description || '', {
                        ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'ul', 'ol', 'li'],
                        ALLOWED_ATTR: [],
                      })
                    }}
                  />

                  <div className="space-y-3 mb-6">
                    {product.courses?.[0] && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <MapPin className="w-4 h-4 text-[#F39800]" />
                        <span>{product.courses[0].name}</span>
                        {product.courses[0].difficulty && (
                          <span className="bg-gray-100 px-2 py-1 rounded text-xs">
                            {product.courses[0].difficulty}
                          </span>
                        )}
                      </div>
                    )}

                    {product.nearest_trip && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar className="w-4 h-4 text-[#F39800]" />
                        <span>출발일: {formatDate(product.nearest_trip.departure_date)}</span>
                      </div>
                    )}

                    {product.duration && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Clock className="w-4 h-4 text-[#F39800]" />
                        <span>{product.duration}</span>
                      </div>
                    )}

                    {product.nearest_trip && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Users className="w-4 h-4 text-[#F39800]" />
                        <span>
                          현재 {product.nearest_trip.booking_count}명 예약
                          {product.nearest_trip.min_participants && ` (최소 ${product.nearest_trip.min_participants}명)`}
                        </span>
                      </div>
                    )}
                  </div>

                  {product.courses?.[0]?.course_id ? (
                    <Link
                      to={`/product-detail/${product.courses[0].course_id}`}
                      className="block w-full bg-primary hover:bg-primary-600 hover:text-white text-white text-center py-3 rounded-lg font-semibold transition-colors"
                    >
                      상세보기 및 예약
                    </Link>
                  ) : (
                    <div className="block w-full bg-gray-400 text-white text-center py-3 rounded-lg font-semibold cursor-not-allowed">
                      코스 정보 불러오는 중...
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="bg-white rounded-2xl p-8 shadow-lg max-w-md mx-auto">
              <div className="text-6xl mb-4">🚌</div>
              <h4 className="text-xl font-bold text-gray-800 mb-3">
                곧 새로운 투어가 오픈됩니다!
              </h4>
              <p className="text-gray-600 mb-6">
                현재 예약 가능한 투어가 준비 중입니다.<br />
                조금만 기다려주세요.
              </p>
              <Link
                to="/courses"
                className="inline-block bg-primary hover:bg-primary-600 hover:text-white text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                모든 코스 둘러보기
              </Link>
            </div>
          </div>
        )}

        {/* 이번 달 전체 투어 일정 - 서브 섹션 */}
        <div className="mt-16 pt-12 border-t border-gray-200">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <span>📅</span>
              <span>한 달 일정</span>
            </div>
            <h3 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              지금 바로 원하는 날짜에 예약하세요!
            </h3>
            <p className="text-lg text-gray-600">
              다음 4주간 출발하는 모든 트레킹 투어를 한눈에 확인하세요
            </p>
          </div>
          
          {/* CompactTourCalendar 내용만 가져오기 */}
          <CompactTourCalendar />
        </div>

        {/* 통합된 모든 코스 둘러보기 */}
        <div className="text-center mt-12">
          <Link
            to="/courses"
            className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-800 px-8 py-3 rounded-lg font-semibold transition-colors border border-gray-200"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <MapPin className="w-4 h-4" />
            모든 코스 둘러보기
          </Link>
        </div>
      </div>
    </section>
  );
};

export default RecommendedCourses;
